'use client';

import { ReactNode, useEffect, useRef, useState } from 'react';

import { useClearQueryCache } from '@/shared/hooks/useClearQueryCache';
import { authStorage } from '@/shared/libs/authStorage';
import logger from '@/shared/libs/logger.client';
import { useAuthStore } from '@/shared/store/authStore';

type AuthProviderProps = {
  children: ReactNode;
  fallback?: ReactNode;
};

/**
 * AuthProvider
 *
 * Bootstraps the client-side auth state on app mount using the persisted
 * Zustand store (access token + user). The backend does not expose a `/me`
 * endpoint, so we trust the persisted state here; any subsequent API call
 * will 401 and trigger a silent refresh via `httpClient` if the token has
 * actually expired — or clear the session if the refresh fails.
 *
 * IMPORTANT: we wait for Zustand's `persist` middleware to finish rehydrating
 * from localStorage before taking any decision. Otherwise the effect can run
 * before hydration and wrongly conclude that the session is empty, which
 * would log the user out on every page refresh.
 */
const AuthProvider = ({ children, fallback }: AuthProviderProps) => {
  const hasBootstrapped = useRef(false);

  const { isInitializing, setInitializing } = useAuthStore();
  const clearCache = useClearQueryCache();

  // Track the Zustand persist hydration state.
  const [isHydrated, setIsHydrated] = useState<boolean>(() =>
    typeof window === 'undefined' ? false : useAuthStore.persist.hasHydrated(),
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // If hydration already happened, flip the flag immediately.
    if (useAuthStore.persist.hasHydrated()) {
      setIsHydrated(true);
      return;
    }

    const unsub = useAuthStore.persist.onFinishHydration(() => setIsHydrated(true));
    return () => {
      unsub?.();
    };
  }, []);

  useEffect(() => {
    if (!isHydrated || hasBootstrapped.current) return;
    hasBootstrapped.current = true;

    (async () => {
      try {
        // Read the latest state directly from the store to avoid any stale
        // closure over the `user` value captured at first render.
        const { user, setAccessToken, setUser, clearSession } = useAuthStore.getState();
        const persistedToken = authStorage.getAccessToken();

        if (!persistedToken) {
          logger.info('[AuthProvider] No token found → anonymous session');
          /*
            **On n'efface que s'il y a quelque chose à effacer** (#1567).

            L'effacement garde tout son sens quand une session précédente subsiste : la donnée d'un
            utilisateur ne doit pas survivre à sa déconnexion. Il n'en a aucun sur un démarrage à
            froid — et c'est justement là qu'il faisait des dégâts.

            `qc.clear()` vide toutes les requêtes sous un arbre en cours de rendu : les composants
            qui les observaient se redessinent, leurs abonnements se détachent, et un démontage
            concurrent finit sur un nœud déjà retiré — le `removeChild` de `null` qui laissait une
            page blanche après la connexion.

            La condition lit l'état **au moment du geste**, pas la valeur capturée à l'entrée : entre
            la lecture du jeton et ici, une connexion a pu aboutir.
          */
          const { user: current, accessToken: currentToken } = useAuthStore.getState();
          if (current || currentToken) {
            clearSession();
            await clearCache();
          }
          return;
        }

        if (!user) {
          logger.warn('[AuthProvider] Access token found but user missing → clearing');
          clearSession();
          await clearCache();
          return;
        }

        // Re-sync the store so that the derived `isAuthenticated` flag is up
        // to date (it is only recomputed when setUser / setAccessToken run).
        setAccessToken(persistedToken);
        setUser(user);
        logger.info('[AuthProvider] Session restored from persisted state', { id: user.id });
      } catch (error) {
        logger.error('[AuthProvider] Unexpected error during bootstrap', { error });
        useAuthStore.getState().clearSession();
        await clearCache();
      } finally {
        logger.info('[AuthProvider] Initialization finished');
        setInitializing(false);
      }
    })();
  }, [isHydrated, clearCache, setInitializing]);

  if (isInitializing) {
    return <>{fallback ?? null}</>;
  }

  return <>{children}</>;
};

export default AuthProvider;
